import { useEffect } from "react"
import { useGLTF } from "@react-three/drei"

import { Canvas } from "./components/Canvas/Canvas"
import { getAvatarMesh } from "./components/Avatar/avatarMeshes"
import { AvatarMenu } from "./components/UI/AvatarMenu"
import { ElectionPortalPrompt } from "./components/UI/ElectionPortalPrompt"
import { HUD } from "./components/UI/HUD"
import { IncarnationSelector } from "./components/UI/IncarnationSelector"
import { MobileActionButtons } from "./components/UI/MobileActionButtons"
import { PeerInfoPanel } from "./components/UI/PeerInfoPanel"
import { VoidIntro } from "./components/UI/VoidIntro"
import { useAuth } from "./hooks/useAuth"
import { useFpCameraInput } from "./hooks/useFpCameraInput"
import { useLink } from "./hooks/useLink"
import { useSocket } from "./hooks/useSocket"
import { useAuthStore } from "./stores/useAuthStore"
import { useInspectStore } from "./stores/useInspectStore"
import { useLinkStore } from "./stores/useLinkStore"
import { usePlayerStore } from "./stores/usePlayerStore"

// 2026-05-05 — Preload de los .glb de Cristal mientras el Tripulante
// pasa por el Estado Cero (orbe + intro narrativa + selector). Para
// el momento en que confirma la firma y arranca el descenso, el .glb
// ya está descargado y parseado en memoria — el Suspense interno de
// MaterializedMesh no genera el "Page Unresponsive" del primer frame.
//
// 2026-05-06 — Las URLs se leen directo de avatarMeshes para evitar
// drift: cuando Zak bumpea el `?v=` en avatarMeshes.ts, el preload
// también lo recibe automáticamente. Antes las URLs estaban hardcoded
// acá con un sufijo viejo y el preload no aplicaba al cache.
const ALFA_URL = getAvatarMesh("cristal", "alfa")?.url
const OMEGA_URL = getAvatarMesh("cristal", "omega")?.url
if (ALFA_URL) useGLTF.preload(ALFA_URL)
if (OMEGA_URL) useGLTF.preload(OMEGA_URL)

export default function App() {
    useAuth() // bootstrap único — escribe en useAuthStore
    const auth = useAuthStore()
    const socket = useSocket(auth.token)
    useLink(socket.connected) // suscripción a eventos LINK_* + WEBRTC_*
    useFpCameraInput() // 2026-05-05 v7 — drag rota cámara en FP

    /* selfName: el server hidrata el `displayName` del PlayerSnapshot
       leyendo profiles.full_name desde Supabase (que respeta el
       preferredName que el Tripulante editó en Mi Núcleo). El JWT
       claim `displayName` queda como fallback hasta que el server
       responda con ROOM_STATE. */
    const selfName = usePlayerStore((s) => s.displayName)
    /* PortraitRotateOverlay removido por decisión de Zak (2026-05-04).
       El layout vertical ahora funciona bien también — insistir con
       un overlay quita más de lo que aporta. Si el Tripulante rota
       a horizontal, el HUD ya está preparado; si no, juega vertical
       sin avisos. */

    /* 2026-05-05 — Auto-apertura del PeerInfoPanel cuando se sella un
       enlace. Reemplaza al TelemetryHologram 3D que vivía dentro del
       Canvas (removido en LinkScene 2026-05-05). En cuanto la fase
       pasa a sealed, abrimos el panel 2D del peer enlazado con su
       Índice de Silicio + 6 pilares. Cuando el enlace termina (idle)
       cerramos el panel solo si era ese peer (si el Tripulante abrió
       otro panel manualmente, lo respetamos).

       2026-05-06 — Sumamos: durante reaching o sealing cerramos el
       panel inmediatamente. Si el Tripulante había tapeado al peer
       para leer su telemetría y desde ahí pidió enlace, el modal con
       backdrop oscuro se quedaba encima del Canvas tapando el haz
       cyan que sale de su avatar. Cerrarlo al instante deja el campo
       limpio mientras la solicitud cocina. */
    const linkPhase = useLinkStore((s) => s.phase)
    const linkPeerId = useLinkStore((s) => s.peerId)
    useEffect(() => {
        const inspect = useInspectStore.getState()
        if (linkPhase === "sealed" && linkPeerId) {
            if (inspect.inspectId !== linkPeerId) {
                inspect.open(linkPeerId)
            }
        } else if (linkPhase === "reaching" || linkPhase === "sealing") {
            if (inspect.inspectId && inspect.inspectId !== "self") {
                inspect.close()
            }
        } else if (linkPhase === "idle") {
            if (linkPeerId && inspect.inspectId === linkPeerId) {
                inspect.close()
            }
        }
    }, [linkPhase, linkPeerId])

    return (
        <div className="w-screen h-screen relative bg-deep-space overflow-hidden">
            <Canvas />
            <HUD
                authStatus={auth.status}
                connected={socket.connected}
                roomId={socket.roomId}
                selfName={
                    /* Prioridad: store hidratado por el server →
                       claim del JWT → null. La store arranca en
                       "Tripulante" antes de que el server responda;
                       en ese caso usamos el claim como fallback
                       transitorio. */
                    selfName && selfName !== "Tripulante"
                        ? selfName
                        : (auth.claims?.displayName ?? null)
                }
                peerCount={socket.peerCount}
                error={socket.error ?? auth.error ?? null}
            />
            {/* Sprint 6 — intro narrativa del Estado Cero. Aparece la
                primera vez que el Tripulante entra al void. La
                reconfiguración salta directo al selector. */}
            <VoidIntro />
            {/* Sprint 3 — overlay glassmorphism que aparece cuando el
                Tripulante está en void (sin modelo elegido aún). Se
                desmonta solo al confirmar y dispara la SPAWN_REQUEST. */}
            <IncarnationSelector />
            {/* Sprint 5 — pill top-right con la firma actual + botón
                "Elegir avatar" que dispara RECONFIGURE_REQUEST y vuelve
                al vacío. Visible solo cuando phase === "incarnated". */}
            <AvatarMenu />
            {/* 2026-05-05 — Panel 2D de inspección de avatares. Se abre
                tap a un avatar (propio o ajeno) o automáticamente al
                sellarse un enlace. Sustituye al TelemetryHologram 3D. */}
            <PeerInfoPanel />
            {/* 2026-05-06 — Prompt de Reconfiguración. Aparece cuando
                el avatar pisa el portal dorado lateral. Tap → vuelve
                al rito del Estado Cero para elegir una firma nueva. */}
            <ElectionPortalPrompt />
            {/* 2026-05-06 — Botones gestuales para mobile (correr,
                saltar, saludar). Solo se renderean en mobile y solo
                cuando phase === "incarnated". Disparan custom events
                que LocalAvatar escucha y traduce a sus refs internos. */}
            <MobileActionButtons />
        </div>
    )
}
